import React, { ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { CacheProvider, EmotionCache } from '@emotion/react';

// Derived from the provider itself so callers pass their own store instance (server: per-request,
// client: the singleton) without re-stating redux internals here.
type ReduxStore = React.ComponentProps<typeof Provider>['store'];

interface AppProvidersProps {
  emotionCache: EmotionCache;
  store: ReduxStore;
  /** The server passes its per-request Helmet context; the client omits it (defaults internally). */
  helmetContext?: object;
  /**
   * Router strategy — the one part of the shell that legitimately differs between environments:
   * `StaticRouter` on the server, `BrowserRouter` on the client. Any router-adjacent, environment-only
   * provider (e.g. the server's `StatusCodeProvider`) is composed inside this wrapper by the caller.
   */
  renderRouter: (children: ReactNode) => ReactNode;
  children: ReactNode;
}

// SHARED (isomorphic): the provider shell used by BOTH the SSR render (`ServerAppTree`) and client
// hydration (`ThemedAppClientRoot`). Only the truly-shared, order-aligned providers live here
// (Emotion cache → redux store → Helmet → router). Environment-only providers stay in each entry:
// the server adds `StyleSheetManager` + `StatusCodeProvider`; the client adds `StrictMode` + its
// browser providers (Auth0, tracking, …). This single owner is what stops the two trees drifting.
export const AppProviders = ({ emotionCache, store, helmetContext, renderRouter, children }: AppProvidersProps) => (
  <CacheProvider value={emotionCache}>
    <Provider store={store}>
      <HelmetProvider context={helmetContext}>{renderRouter(children)}</HelmetProvider>
    </Provider>
  </CacheProvider>
);
